import { useLayoutEffect } from 'react'

/* ------------------------------------------------------------------ *
 *  Head management
 *
 *  This replaces react-helmet-async, which rendered nothing at all in a
 *  production build of this project: the dev server showed every page's
 *  title, description, canonical and JSON-LD, and the built site showed
 *  none of them — not even a <Helmet> placed directly inside the provider
 *  in main.jsx. Since <Seo> and the structured-data components were the
 *  only consumers, and every tag they emit is a plain element with plain
 *  attributes, a direct implementation is both smaller and verifiable.
 *
 *  Behaviour that matters for SEO:
 *
 *    - A tag we manage REPLACES an existing one with the same identity
 *      (<title>, meta by name/property, link by rel) rather than sitting
 *      beside it, so a crawler never sees two descriptions. The one that
 *      was there — the prerendered tag baked in at build time — is put
 *      back when the component unmounts.
 *    - Everything runs in useLayoutEffect, so the head is correct before
 *      the browser paints and before any scraper that waits for load.
 *
 *  Usage:
 *
 *    useHead([
 *      { tag: 'title', text: 'About — Spectranox' },
 *      { tag: 'meta', attrs: { name: 'description', content: '...' } },
 *      { tag: 'link', attrs: { rel: 'canonical', href: '...' } },
 *      { tag: 'script', attrs: { type: 'application/ld+json' }, text: '{...}' },
 *    ])
 * ------------------------------------------------------------------ */

/** What makes two head tags "the same tag" for replacement purposes. */
function selectorFor(d) {
  if (d.tag === 'title') return 'title'
  if (d.tag === 'meta') {
    if (d.attrs?.name) return `meta[name="${CSS.escape(d.attrs.name)}"]`
    if (d.attrs?.property) return `meta[property="${CSS.escape(d.attrs.property)}"]`
    return null
  }
  if (d.tag === 'link' && d.attrs?.rel) return `link[rel="${CSS.escape(d.attrs.rel)}"]`
  /* JSON-LD blocks are additive: a page can carry several, and none of
     them replaces another. */
  return null
}

export function useHead(descriptors) {
  /* The descriptors are rebuilt on every render, so compare by value.
     Without this the effect would tear the head down and rebuild it on
     each render, which browsers show as a flickering tab title. */
  const key = JSON.stringify(descriptors)

  useLayoutEffect(() => {
    const list = JSON.parse(key)
    const head = document.head
    const added = []
    const displaced = []

    for (const d of list) {
      if (!d || !d.tag) continue

      const sel = selectorFor(d)
      if (sel) {
        for (const existing of head.querySelectorAll(sel)) {
          /* Never displace a tag another mounted component is managing —
             that one will clean up after itself. */
          if (existing.dataset.sxHead) continue
          displaced.push([existing, existing.nextSibling])
          existing.remove()
        }
      }

      const el = document.createElement(d.tag)
      for (const [k, v] of Object.entries(d.attrs || {})) {
        if (v !== undefined && v !== null && v !== false) el.setAttribute(k, String(v))
      }
      if (d.text !== undefined) el.textContent = String(d.text)
      el.dataset.sxHead = 'true'
      head.appendChild(el)
      added.push(el)
    }

    return () => {
      for (const el of added) el.remove()
      /* Put the build-time tags back, in their original position, so a
         client-side navigation away from a page does not leave the head
         emptier than it was served. */
      for (const [el, before] of displaced) {
        head.insertBefore(el, before && before.parentNode === head ? before : null)
      }
    }
  }, [key])
}

export default useHead
